/**
 * Review & Publish domain types for AuditWhiz (WHIZ-002).
 *
 * These reuse the Helios question contract (`src/types/helios/Question.ts`)
 * rather than re-declaring its fields, so there's a single source of truth for
 * the question shape and the Phase 3 publish mapping onto `CreateQuestionRequest`
 * is near 1:1. A review draft is "a question's editable content + config, plus
 * review-screen metadata (status, wizard step, the new Source Policy ID)".
 *
 * Phase 1 shell: populated from hardcoded mock data. Phase 2 populates from the
 * Astra agent response; Phase 3 maps to the Helios create/update requests.
 */
import type {
  QuestionContent,
  SubQuestion,
  TaskDetails,
  DocLink,
  TagMap,
} from 'src/types/helios/Question';

/**
 * Review status of a single Astra-drafted question in the side panel.
 * - NEEDS_REVIEW: untouched draft.
 * - PUBLISHED: saved & published — becomes Active, gets a Question ID, and is
 *   no longer editable or deletable from the review screen.
 *
 * (The P1 "UPDATED_NOT_SAVED" state is deferred per requirements.md; only the
 * visual distinction is MLP and is tracked in Phase 2.)
 */
export enum ReviewQuestionStatus {
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  PUBLISHED = 'PUBLISHED',
}

/** The three review wizard steps, in order. */
export enum ReviewWizardStep {
  AUDIT_CRITERIA = 'AUDIT_CRITERIA',
  SUB_QUESTIONS = 'SUB_QUESTIONS',
  QUESTION_CONFIGURATION = 'QUESTION_CONFIGURATION',
}

export const REVIEW_WIZARD_STEP_ORDER: readonly ReviewWizardStep[] = [
  ReviewWizardStep.AUDIT_CRITERIA,
  ReviewWizardStep.SUB_QUESTIONS,
  ReviewWizardStep.QUESTION_CONFIGURATION,
];

// Re-export the Helios shapes the review surfaces edit, so callers import the
// question contract from one place and we don't fork field definitions.
export type { QuestionContent, SubQuestion, TaskDetails, DocLink, TagMap };

/**
 * Question-level configuration edited in the "Question Configuration" step.
 * Every field maps directly to a `Question` / `CreateQuestionRequest` field of
 * the same name — except `sourcePolicyId`, the one genuinely-new field in the
 * MLP (WHIZ-002 AC7), auto-populated for Astra questions.
 */
export interface ReviewQuestionConfiguration {
  /** Maps to `QuestionDetails.title`. */
  readonly overview: string;
  readonly businessUnit: string;
  readonly divisionName: string;
  readonly categories: string[];
  readonly auditScore: number;
  readonly tagsIncluded: TagMap;
  /** Tag leaves explicitly excluded (optional — mirrors Helios
   *  `Question.tagsExcluded`). The config UI offers this only once
   *  `tagsIncluded` is non-empty, and the two selections are mutually
   *  exclusive at the (group, value) leaf level. */
  readonly tagsExcluded?: TagMap;
  readonly locationsExcluded: string[];
  /** NEW field (WHIZ-002 AC7) — auto-populated from the source policy. */
  readonly sourcePolicyId: string;
  readonly requireCommentOnFail: boolean;
  readonly requireEvidenceOnFail: boolean;
  readonly requireCommentOnPass: boolean;
  readonly requireEvidenceOnPass: boolean;
  readonly docLinks: DocLink[];
  /**
   * ERT: whether sub-questions are shown conditionally on the parent's
   * Pass/Fail result. Maps to `Question.hasConditionalSubQuestions`. Can only
   * be enabled when every sub-question is a data-collection type (a Pass/Fail
   * sub can't be conditional — the trigger IS the parent result); the Sub
   * Questions step enforces that gate.
   */
  readonly hasConditionalSubQuestions?: boolean;
}

/**
 * A single Astra-drafted question under review.
 *
 * `content` is the Helios `QuestionContent` (questionDetails + optional
 * taskDetails + subQuestions) — the same shape the manual flow assembles — so
 * the Audit Criteria and Sub Questions steps edit the real contract directly.
 * `configuration` carries the question-level fields plus review metadata.
 */
export interface ReviewQuestion {
  /** Stable client-side id for selection/keys (not the published Question ID). */
  readonly id: string;
  /** Published Question ID (e.g. "Q-1000"), present only when PUBLISHED. */
  readonly questionId?: string;
  readonly status: ReviewQuestionStatus;
  /** Side-panel display title (mirrors `content.questionDetails.title`). */
  readonly title: string;
  readonly content: QuestionContent;
  readonly configuration: ReviewQuestionConfiguration;
}

/**
 * One-shot navigation payload handed from the generate screen to the review
 * screen via React Router location `state`. Carries the SOP link the author
 * entered on the prompt step so the review screen can prefill each drafted
 * question's Standard Operating Procedure field. Optional end to end: direct
 * navigation to the review route arrives with no state and prefills nothing.
 */
export interface GenerateToReviewNavState {
  /** SOP reference link from the generate screen, or null when none was set. */
  readonly sopLink: string | null;
}
